import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, ChevronRight, Plus, XCircle, Ban, DollarSign, Info } from "lucide-react";

/**
 * Iter P2.1a — Job Costing Foundation cost-sheet UI.
 *
 * Visible only when the business feature flag `job_costing_settings.enabled`
 * is true AND the caller has admin+ role. Backend enforces both; this UI hide
 * is defense-in-depth.
 *
 * Never surfaced when `tracked === false`; owners see an inline import prompt
 * instead so an empty state is actionable, not blank.
 */

const CATEGORIES = [
  "labor", "materials", "chemicals", "fuel", "mileage",
  "equipment", "subcontractor", "disposal", "permits", "other",
];
const CATEGORY_LABELS = {
  labor: "Labor", materials: "Materials", chemicals: "Chemicals",
  fuel: "Fuel", mileage: "Mileage", equipment: "Equipment",
  subcontractor: "Subcontractors", disposal: "Disposal",
  permits: "Permits", other: "Other",
};
const SOURCE_LABELS = {
  estimate_import: "Estimate Import", manual: "Manual Entry",
  time_clock: "Time Clock", wage_config: "Wage Config",
  inventory_usage: "Inventory", mileage_source: "Mileage",
  equipment_rental: "Equipment Rental", purchase_order: "Purchase Order",
};
const CONFIDENCE_LABELS = { automatic: "Automatic", imported: "Imported", manual: "Manual" };

const fmtUsd = (n) =>
  (Number(n || 0)).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const fmtPct = (n) => (n == null ? "—" : `${n}%`);

export default function JobCostSheet({ jobId, jobHasEstimate }) {
  const [state, setState] = useState({ loading: true, enabled: false, tracked: false, sheet: null, totals: null });
  const [open, setOpen] = useState(true);
  const [newRow, setNewRow] = useState({ category: "labor", kind: "actual", amount: "", hours: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}/cost-sheet`);
      if (data.enabled === false) {
        setState({ loading: false, enabled: false, tracked: false, sheet: null, totals: null });
        return;
      }
      setState({ loading: false, enabled: true, tracked: !!data.tracked, sheet: data.sheet || null, totals: data.totals || null });
    } catch (e) {
      // 403 → user lacks admin role. Hide silently — never leak the surface.
      setState({ loading: false, enabled: false, tracked: false, sheet: null, totals: null });
    }
  };

  useEffect(() => { if (jobId) load(); }, [jobId]); // eslint-disable-line

  if (state.loading || !state.enabled) return null;

  const importFromEstimate = async () => {
    if (!jobHasEstimate) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/cost-sheet/import-from-estimate`, { mode: "import" });
      toast.success("Costs imported from estimate");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Import failed";
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const startBlank = async () => {
    setBusy(true);
    try {
      // Create an empty row + delete it = effectively creates the sheet.
      // Simpler: hit import with mode=blank.
      if (jobHasEstimate) {
        await api.post(`/jobs/${jobId}/cost-sheet/import-from-estimate`, { mode: "blank" });
      } else {
        // No estimate → seed with a single $0 "other" row, user can edit/void.
        await api.post(`/jobs/${jobId}/cost-sheet/rows`, {
          category: "other", kind: "estimated", amount: 0,
        });
      }
      toast.success("Cost sheet started");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start cost sheet");
    } finally { setBusy(false); }
  };

  const addRow = async () => {
    if (newRow.amount === "" || Number(newRow.amount) < 0) {
      toast.error("Amount must be 0 or greater"); return;
    }
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/cost-sheet/rows`, {
        category: newRow.category,
        kind: newRow.kind,
        amount: Number(newRow.amount),
        hours: newRow.hours === "" ? null : Number(newRow.hours),
      });
      setNewRow({ category: "labor", kind: "actual", amount: "", hours: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally { setBusy(false); }
  };

  const voidRow = async (row) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/cost-sheet/rows/${row.id}/void`);
      await load();
    } catch { toast.error("Void failed"); } finally { setBusy(false); }
  };

  const deleteRow = async (row) => {
    if (!window.confirm("Delete this cost row? Voiding is usually preferred so the audit trail is preserved.")) return;
    setBusy(true);
    try {
      await api.delete(`/jobs/${jobId}/cost-sheet/rows/${row.id}`);
      await load();
    } catch { toast.error("Delete failed"); } finally { setBusy(false); }
  };

  // Empty state — flag on, no sheet yet
  if (!state.tracked) {
    return (
      <div
        className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp"
        data-testid="job-costs-empty"
      >
        <div className="flex items-center gap-2 mb-2">
          <DollarSign className="w-5 h-5 text-[#FF5A00]" />
          <h2 className="font-heading text-xl font-bold">Job costs</h2>
        </div>
        <div className="text-sm text-zinc-500 mb-4">
          This job isn't tracking costs yet. Costs are optional and never affect invoicing.
        </div>
        <div className="flex gap-2 flex-wrap">
          {jobHasEstimate && (
            <Button
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
              onClick={importFromEstimate}
              disabled={busy}
              data-testid="job-costs-import-btn"
            >Import from estimate</Button>
          )}
          <Button variant="outline" onClick={startBlank} disabled={busy} data-testid="job-costs-blank-btn">
            Start blank cost sheet
          </Button>
        </div>
      </div>
    );
  }

  const totals = state.totals || {};
  const est = totals.estimated || {};
  const act = totals.actual || {};
  const variance = totals.variance || {};
  const prof = totals.profitability || {};
  const visibleCategories = CATEGORIES.filter((c) =>
    (est[c] || 0) > 0 || (act[c] || 0) > 0 ||
    (state.sheet?.rows || []).some((r) => r.category === c && !r.voided),
  );

  return (
    <div
      className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp"
      data-testid="job-costs-sheet"
    >
      <button
        className="flex items-center gap-2 mb-4 w-full text-left"
        onClick={() => setOpen((v) => !v)}
        data-testid="job-costs-toggle"
      >
        {open ? <ChevronDown className="w-5 h-5 text-zinc-500" /> : <ChevronRight className="w-5 h-5 text-zinc-500" />}
        <DollarSign className="w-5 h-5 text-[#FF5A00]" />
        <h2 className="font-heading text-xl font-bold">Job costs</h2>
        <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 font-mono">
          Estimated vs Actual
        </span>
      </button>

      {open && (
        <div>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="job-costs-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-200">
                  <th className="pb-2 pr-3">Category</th>
                  <th className="pb-2 pr-3 text-right">Estimated</th>
                  <th className="pb-2 pr-3 text-right">Actual</th>
                  <th className="pb-2 pr-3 text-right">Variance</th>
                </tr>
              </thead>
              <tbody>
                {visibleCategories.length === 0 && (
                  <tr><td colSpan={4} className="py-3 text-zinc-400 italic">No rows yet — use the row builder below.</td></tr>
                )}
                {visibleCategories.map((c) => (
                  <tr key={c} className="border-b border-zinc-100" data-testid={`jc-cat-row-${c}`}>
                    <td className="py-2 pr-3 font-medium">{CATEGORY_LABELS[c]}</td>
                    <td className="py-2 pr-3 text-right" data-testid={`jc-est-${c}`}>{fmtUsd(est[c])}</td>
                    <td className="py-2 pr-3 text-right" data-testid={`jc-act-${c}`}>{fmtUsd(act[c])}</td>
                    <td className={`py-2 pr-3 text-right ${((variance[c] || 0) < 0) ? "text-rose-600" : "text-emerald-700"}`} data-testid={`jc-var-${c}`}>
                      {fmtUsd(variance[c])}
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold border-t-2 border-zinc-300">
                  <td className="py-2 pr-3">Totals</td>
                  <td className="py-2 pr-3 text-right" data-testid="jc-est-total">{fmtUsd(est.total)}</td>
                  <td className="py-2 pr-3 text-right" data-testid="jc-act-total">{fmtUsd(act.total)}</td>
                  <td className={`py-2 pr-3 text-right ${((variance.total || 0) < 0) ? "text-rose-600" : "text-emerald-700"}`} data-testid="jc-var-total">
                    {fmtUsd(variance.total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Profitability */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="job-costs-profitability">
            <ProfitTile label="Revenue (invoiced)" value={fmtUsd(prof.invoice_total)} testid="jc-prof-revenue" />
            <ProfitTile label="Estimated profit" value={prof.estimated_gross_profit == null ? "—" : fmtUsd(prof.estimated_gross_profit)} sub={prof.estimated_margin_pct == null ? null : `margin ${fmtPct(prof.estimated_margin_pct)}`} testid="jc-prof-est" />
            <ProfitTile label="Actual profit" value={prof.actual_gross_profit == null ? "—" : fmtUsd(prof.actual_gross_profit)} sub={prof.actual_margin_pct == null ? null : `margin ${fmtPct(prof.actual_margin_pct)}`} testid="jc-prof-act" />
            <ProfitTile label="Cash-basis actual" value={prof.cash_basis?.actual_gross_profit == null ? "—" : fmtUsd(prof.cash_basis.actual_gross_profit)} sub={prof.cash_basis?.amount_paid == null ? null : `paid ${fmtUsd(prof.cash_basis.amount_paid)}`} testid="jc-prof-cash" />
          </div>

          {/* Row list w/ source + confidence */}
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">Rows</div>
            <div className="space-y-1">
              {(state.sheet?.rows || []).map((r) => (
                <div
                  key={r.id}
                  className={`flex flex-wrap items-center gap-3 border rounded px-3 py-2 ${r.voided ? "border-zinc-200 bg-zinc-50 opacity-60" : "border-zinc-200 bg-white"}`}
                  data-testid={`jc-row-${r.id}`}
                >
                  <span className="text-[10px] uppercase font-mono text-zinc-500 w-14">
                    {r.kind === "estimated" ? "Est." : "Act."}
                  </span>
                  <span className="text-sm font-medium w-24">{CATEGORY_LABELS[r.category] || r.category}</span>
                  <span className="text-sm w-24">{fmtUsd(r.amount)}</span>
                  {r.hours != null && (
                    <span className="text-xs text-zinc-500 w-20">{r.hours} hrs</span>
                  )}
                  <span className="text-[11px] text-zinc-500">{SOURCE_LABELS[r.source] || r.source}</span>
                  <span className="text-[11px] text-zinc-400">· {CONFIDENCE_LABELS[r.confidence] || r.confidence}</span>
                  {r.voided ? (
                    <span className="text-[11px] text-rose-600 italic">voided</span>
                  ) : (
                    <div className="ml-auto flex gap-1">
                      <button onClick={() => voidRow(r)} title="Void" data-testid={`jc-row-void-${r.id}`} className="p-1 text-amber-600 hover:bg-amber-50 rounded">
                        <Ban className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteRow(r)} title="Delete" data-testid={`jc-row-del-${r.id}`} className="p-1 text-rose-600 hover:bg-rose-50 rounded">
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {(state.sheet?.rows || []).length === 0 && (
                <div className="text-sm text-zinc-500 italic">No rows yet.</div>
              )}
            </div>
          </div>

          {/* Add row */}
          <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-zinc-100 pt-4" data-testid="job-costs-add-row">
            <div>
              <div className="text-[10px] uppercase font-mono text-zinc-500 mb-1">Kind</div>
              <Select value={newRow.kind} onValueChange={(v) => setNewRow({ ...newRow, kind: v })}>
                <SelectTrigger className="w-32" data-testid="jc-new-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="estimated">Estimated</SelectItem>
                  <SelectItem value="actual">Actual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase font-mono text-zinc-500 mb-1">Category</div>
              <Select value={newRow.category} onValueChange={(v) => setNewRow({ ...newRow, category: v })}>
                <SelectTrigger className="w-40" data-testid="jc-new-cat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase font-mono text-zinc-500 mb-1">Amount ($)</div>
              <Input type="number" step="0.01" min="0" value={newRow.amount}
                     onChange={(e) => setNewRow({ ...newRow, amount: e.target.value })}
                     className="w-32" data-testid="jc-new-amount" />
            </div>
            {newRow.category === "labor" && (
              <div>
                <div className="text-[10px] uppercase font-mono text-zinc-500 mb-1">Hours</div>
                <Input type="number" step="0.25" min="0" value={newRow.hours}
                       onChange={(e) => setNewRow({ ...newRow, hours: e.target.value })}
                       className="w-24" data-testid="jc-new-hours" />
              </div>
            )}
            <Button onClick={addRow} disabled={busy} data-testid="jc-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add row
            </Button>
          </div>

          {state.sheet?.imported_from_estimate_id && (
            <div className="mt-4 text-xs text-zinc-500 flex items-center gap-2">
              <Info className="w-3.5 h-3.5" />
              Imported from estimate rev {state.sheet.imported_estimate_revision ?? 1}
              {state.sheet.imported_at ? ` on ${new Date(state.sheet.imported_at).toLocaleDateString()}` : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProfitTile({ label, value, sub, testid }) {
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3" data-testid={testid}>
      <div className="text-[10px] uppercase font-mono tracking-widest text-zinc-500">{label}</div>
      <div className="text-lg font-semibold text-zinc-900 mt-0.5">{value}</div>
      {sub ? <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div> : null}
    </div>
  );
}
